<template>
  <div class="pa-4">
    <div v-if="loading">
      <v-skeleton-loader type="date-picker"></v-skeleton-loader>
    </div>
    <div v-else>
      <v-tabs v-model="activeTab" show-arrows>
        <v-tab :key="'public_schedule'" ripple>{{ $t('message.public_schedule') }}</v-tab>
        <v-tab :key="'schedule_notes'" ripple>{{ $t("message.schedule_notes") }}</v-tab>
        <v-tab :key="'embed_Schedule'" ripple>{{ $t("message.embed_schedule") }}</v-tab>

        <v-tabs-items v-model="activeTab">
          <v-tab-item :key="'public_schedule'" class="pa-3">
            <v-card v-if="!loading && academy" class="bjjlink-card" elevation="0" outlined>
              <div xs12 fill-height>
                <schedule-selector v-model="academy.schedule"></schedule-selector>
              </div>
            </v-card>
            <alert-message :text="updateMessage" />
          </v-tab-item>
          <v-tab-item :key="'schedule_notes'" class="pa-3">
            <v-card class="pa-0" v-allowed="'edit_schedule'" elevation="0" outlined>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn outlined small color="primary" @click="updateScheduleNotes('update')">{{ $t('message.update')
                  }}</v-btn>
                <v-btn small @click="updateScheduleNotes()">{{ $t('message.clear') }}</v-btn>
              </v-card-actions>
              <v-card-text>
                <v-avatar size="20" v-if="scheduleNotes && scheduleNotes.user">
                  <img v-if="scheduleNotes.user.userImageSmall" :src="userImg(scheduleNotes.user.userImageSmall)" />
                  <img v-else :src="assets('default-profile-avatar.png')" />
                </v-avatar>
                <span v-if="scheduleNotes && scheduleNotes.user">{{ $t('message.last_update_by') }}
                  {{ scheduleNotes.user.displayName }}</span>
                <span v-if="scheduleNotes && scheduleNotes.user" style="color:gray; margin-left: 10px">{{ $d(new
                  Date(scheduleNotes.date), 'short', $i18n.locale) }}</span>
                <v-textarea style="margin-top:10px" rows="3" :auto-grow="true" outlined v-model="scheduleNotes.notes"
                  :label="$t('message.schedule_notes')"></v-textarea>
                <div class="supportive-text" style="margin-top:-20px">{{ $t('message.schedule_notes_explained') }}</div>
              </v-card-text>
            </v-card>
          </v-tab-item>
          <v-tab-item :key="'embed_Schedule'" class="pa-3">
            <v-card elevation="0" outlined>
              <v-card-actions>
                <v-switch v-model="dark" @change="updatePublicUrl" :label="$t('message.dark')"></v-switch>
              </v-card-actions>
              <v-card-text>
                <v-list-item class="pa-0">
                  <v-text-field :disabled="true" class="clickable" :label="$t('message.public_schedule_url')"
                    v-model="publicScheduleUrl"></v-text-field>
                </v-list-item>
                <span class="supporting-text">{{ $t('message.embed_schedule_explained') }}</span>
              </v-card-text>
              <v-card-text v-if="displayExternalSchedule" id="academy_public_schedule_embed">
                <iframe style="width:100%;height:330px" :src="publicScheduleUrl"></iframe>
              </v-card-text>
              <v-card-text class="text-center" v-else>
                <circles-loader></circles-loader>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn small @click="copyPublicUrl()">
                  {{ $t('message.copy_schedule_url') }}
                </v-btn>
                <v-btn small @click="copyEmbedCode">
                  {{ $t('message.copy_embed_code') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-tabs>
    </div>
  </div>
</template>

<script>
import AlertMessage from "@/components/displays/AlertMessage.vue";
import CirclesLoader from "@/components/displays/CirclesLoader.vue";
import ScheduleSelector from "@/components/inputs/ScheduleSelector.vue";
import Api from "@/services/Api";

export default {
  data() {
    return {
      loading: false,
      academy: null,
      activeTab: 0,
      schedule: null,
      scheduleNotes: {},
      updateMessage: null,
      publicScheduleUrl: null,
      dark: false,
      displayExternalSchedule: true,
    };
  },
  components: {
    CirclesLoader,
    ScheduleSelector,
    AlertMessage,
  },
  created() {
    Api()
      .get(
        "admins/academy/" +
        this.$store.state.selectedAcademy +
        "?populate=schedule_notes"
      )
      .then((response) => {
        this.academy = response.data;
        this.publicScheduleUrl = 'https://core.bjjlink.com/pub/academy/' + this.$store.state.selectedAcademy + '/schedule?' + (this.dark ? 'theme=dark' : '');
        this.scheduleNotes =
          response.data.extraFields && response.data.extraFields.scheduleNotes
            ? response.data.extraFields.scheduleNotes
            : {};
      })
      .catch((e) => {
        this.loading = false;
        if (e)
          this.showAlert("error", "Oops!", this.$t("message.error_generic"));
      });
  },
  methods: {
    updatePublicUrl() {
      this.publicScheduleUrl = 'https://core.bjjlink.com/pub/academy/' + this.$store.state.selectedAcademy + '/schedule?' + (this.dark ? 'theme=dark' : '');
      this.displayExternalSchedule = false;
      const vm = this;
      setTimeout(function () {
        vm.displayExternalSchedule = true;
      }, 1000)
    },
    copyEmbedCode() {
      let textToCopy = '<iframe frameBorder="0" style="width:100%;height:330px" src="' + this.publicScheduleUrl + '"></iframe>';
      try {
        navigator.clipboard.writeText(textToCopy);
        /* Alert the copied text */
        console.log("Copied the text: " + textToCopy);
      } catch (err) {
        this.showAlert("error", this.$t("message.oops_not_working"));
      }
    },
    copyPublicUrl() {
      let urlToCopy = this.publicScheduleUrl;
      this.copyToClipboard(urlToCopy);
    },
    updateScheduleNotes(val) {
      let notes = null;
      if (val) notes = this.scheduleNotes.notes;
      else this.scheduleNotes = {};
      Api()
        .patch(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/schedule/notes",
          {
            notes: notes,
          }
        )
        .then((response) => {
          const data = response.data;
          if (data) {
            this.showAlert("success", this.$t("message.updated"));
          }
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        });
    },
  },
};
</script>

<style scoped></style>
`